import { useTranslationV2 } from "@/hooks/useTranslationV2";

interface Props {
  variant?: "footer" | "inline";
  className?: string;
}

/**
 * Displays accepted payment methods: Visa, Mastercard, Apple Pay, Google Pay.
 * Uses inline SVG logos (no external requests, no auth required).
 * Brand marks follow each provider's display guidelines (white pill, official colors).
 */
export default function PaymentMethodsBadge({ variant = "footer", className = "" }: Props) {
  const { t } = useTranslationV2("common");
  const isFooter = variant === "footer";

  const label =
    t("payment_methods_label") !== "payment_methods_label"
      ? t("payment_methods_label")
      : "Plăți securizate";

  return (
    <div
      className={`flex flex-col items-center gap-2 ${className}`}
      aria-label="Accepted payment methods"
    >
      <span
        className={`text-[11px] uppercase tracking-widest ${
          isFooter ? "text-primary-foreground/60" : "text-muted-foreground"
        }`}
      >
        {label}
      </span>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {/* Visa */}
        <div className="h-7 w-12 rounded-md bg-white flex items-center justify-center shadow-xs">
          <svg viewBox="0 0 48 16" className="h-4 w-auto" aria-label="Visa">
            <text
              x="24"
              y="13"
              textAnchor="middle"
              fontFamily="Arial, sans-serif"
              fontWeight="900"
              fontSize="14"
              fontStyle="italic"
              fill="#1A1F71"
            >
              VISA
            </text>
          </svg>
        </div>

        {/* Mastercard */}
        <div className="h-7 w-12 rounded-md bg-white flex items-center justify-center shadow-xs">
          <svg viewBox="0 0 32 20" className="h-5 w-auto" aria-label="Mastercard">
            <circle cx="12" cy="10" r="7" fill="#EB001B" />
            <circle cx="20" cy="10" r="7" fill="#F79E1B" />
            <path
              d="M16 5.2a7 7 0 0 1 0 9.6 7 7 0 0 1 0-9.6z"
              fill="#FF5F00"
            />
          </svg>
        </div>

        {/* Apple Pay */}
        <div className="h-7 w-12 rounded-md bg-black flex items-center justify-center shadow-xs">
          <svg viewBox="0 0 50 20" className="h-4 w-auto" aria-label="Apple Pay">
            <path
              fill="#fff"
              d="M9.3 5.4c-.5.6-1.3 1.1-2.1 1-.1-.8.3-1.7.8-2.2.5-.6 1.4-1 2.1-1.1.1.9-.3 1.7-.8 2.3zm.8.9c-1.2-.1-2.2.7-2.7.7-.6 0-1.4-.6-2.3-.6-1.2 0-2.3.7-2.9 1.8-1.3 2.2-.3 5.4 1 7.1.6.9 1.4 1.8 2.4 1.8.9 0 1.3-.6 2.4-.6 1.1 0 1.5.6 2.4.6 1 0 1.7-.9 2.3-1.7.7-1 1-1.9 1-2-.1 0-2-.7-2-2.9 0-1.8 1.5-2.7 1.6-2.7-.9-1.3-2.2-1.4-2.7-1.5zM18.2 4.6v11h1.7v-3.8h2.4c2.2 0 3.7-1.5 3.7-3.6 0-2.1-1.5-3.6-3.6-3.6h-4.2zm1.7 1.4h2c1.5 0 2.3.8 2.3 2.2s-.8 2.2-2.3 2.2h-2V6zm10.3 9.7c1.1 0 2.1-.5 2.5-1.4h.1v1.3h1.6V10c0-1.6-1.3-2.6-3.2-2.6-1.8 0-3.1 1-3.2 2.5h1.5c.1-.7.8-1.1 1.6-1.1 1 0 1.6.5 1.6 1.3v.6l-2.1.1c-2 .1-3 .9-3 2.3 0 1.4 1.1 2.4 2.6 2.4zm.5-1.3c-.9 0-1.4-.4-1.4-1.1 0-.7.5-1.1 1.5-1.1l1.9-.1v.6c0 1-.9 1.7-2 1.7zm5.9 4c1.6 0 2.4-.6 3.1-2.6L42.5 7.5h-1.7l-1.9 6.2h-.1l-1.9-6.2H35l2.7 7.6-.1.5c-.3.8-.7 1.2-1.5 1.2-.1 0-.4 0-.5-.1v1.3c.1.1.4.1.7.1z"
            />
          </svg>
        </div>

        {/* Google Pay */}
        <div className="h-7 w-12 rounded-md bg-white flex items-center justify-center shadow-xs border border-black/5">
          <svg viewBox="0 0 60 24" className="h-4 w-auto" aria-label="Google Pay">
            <path
              fill="#5F6368"
              d="M28.4 11.7v4.6h-1.5v-11h3.8c1 0 1.8.3 2.5.9.7.6 1 1.4 1 2.4s-.3 1.8-1 2.4c-.7.6-1.5.9-2.5.9h-2.3v-.2zm0-5v3.6h2.4c.6 0 1.1-.2 1.5-.6.4-.4.6-.8.6-1.3s-.2-.9-.6-1.3c-.4-.4-.9-.6-1.5-.6h-2.4v.2zm9.3 2c1.1 0 1.9.3 2.6.9.6.6.9 1.4.9 2.4v4.9h-1.4v-1.1h-.1c-.6.9-1.4 1.3-2.5 1.3-.9 0-1.6-.3-2.2-.8s-.9-1.2-.9-2c0-.9.3-1.5.9-2 .7-.5 1.6-.8 2.7-.8.9 0 1.7.2 2.3.5v-.3c0-.5-.2-1-.6-1.3-.4-.4-.9-.5-1.5-.5-.8 0-1.5.4-2 1.1l-1.3-.8c.7-1 1.7-1.5 3.1-1.5zm-1.9 5.5c0 .4.2.7.5.9.3.2.7.4 1.2.4.6 0 1.2-.2 1.7-.7s.7-1 .7-1.6c-.5-.4-1.2-.6-2.1-.6-.7 0-1.2.2-1.6.5-.3.3-.4.7-.4 1.1zm12-5.3l-4.7 10.9h-1.5l1.8-3.8-3.1-7.1h1.6l2.2 5.4 2.2-5.4h1.5z"
            />
            <path
              fill="#4285F4"
              d="M20.3 11c0-.5 0-1-.1-1.4h-5.7v2.7h3.3c-.1.8-.6 1.5-1.2 1.9v1.6h2c1.2-1.1 1.7-2.7 1.7-4.8z"
            />
            <path
              fill="#34A853"
              d="M14.5 16.9c1.7 0 3.1-.6 4.1-1.5l-2-1.6c-.5.4-1.2.6-2.1.6-1.6 0-3-1.1-3.5-2.6H9v1.6c1 2 3.1 3.5 5.5 3.5z"
            />
            <path
              fill="#FBBC04"
              d="M11 11.8c-.1-.4-.2-.8-.2-1.2s.1-.8.2-1.2V7.8H9c-.4.8-.6 1.7-.6 2.7s.2 1.9.6 2.7l2-1.4z"
            />
            <path
              fill="#EA4335"
              d="M14.5 7c.9 0 1.7.3 2.4.9l1.8-1.8c-1.1-1-2.5-1.6-4.1-1.6-2.4 0-4.5 1.4-5.5 3.4l2 1.6c.5-1.4 1.9-2.5 3.4-2.5z"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}
